import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Tabs } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { cn } from 'lib/utils';
import { Input } from 'components/ui/input';
import { DESK_NAME } from 'api/sectorAlignmentApi';
import type { DeskAnalysis } from 'api/sectorAlignmentApi';
import ReferentialBoundary from '../components/ReferentialBoundary';
import { filterRows, firstFilledTab, tabRows, TABS, type Tab } from './rows';
import StoresTab from './StoresTab';
import EcmTab from './EcmTab';
import JiraTab from './JiraTab';

/** 3b/3c: Magasins / ECM / À corriger dans Jira over both desks; focus lands on the first non-empty tab. */
const AlignmentTabs = ({ desks }: { desks: DeskAnalysis[] }) => {
  const { t } = useTranslation();
  const rows = useMemo(() => tabRows(desks), [desks]);
  const firstFilled = firstFilledTab({ stores: rows.stores.length, ecm: rows.ecm.length, jira: rows.jira.length });
  const [tab, setTab] = useState<Tab>(firstFilled ?? 'stores');
  const [desk, setDesk] = useState('all');
  const [query, setQuery] = useState('');
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});
  const hadRows = useRef(false);

  // Only when a result appears: a later re-analysis must not pull the focus away.
  useEffect(() => {
    const appeared = Boolean(firstFilled) && !hadRows.current;
    hadRows.current = Boolean(firstFilled);
    if (!appeared || !firstFilled) return;
    setTab(firstFilled);
    tabRefs.current[firstFilled]?.focus();
  }, [firstFilled]);

  if (!firstFilled) return null; // 3d / 9b: nothing to list, no table.
  const shown = filterRows(rows, desk, query);
  const filtered = desk !== 'all' || query.trim() !== '';
  const label: Record<Tab, string> = { stores: t('Stores'), ecm: t('ECM'), jira: t('To correct in Jira') };
  const panels: Record<Tab, ReactNode> = {
    stores: <StoresTab stores={shown.stores} filtered={filtered} />,
    ecm: <EcmTab issues={shown.ecm} filtered={filtered} />,
    jira: <JiraTab fixes={shown.jira} filtered={filtered} />,
  };

  return (
    <Tabs.Root value={tab} onValueChange={(next) => setTab(next as Tab)} className="overflow-hidden rounded-[14px] border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b px-5 py-3.5">
        <Tabs.List aria-label={t('Analysis result')} className="inline-flex h-[38px] gap-0.5 rounded-[9px] bg-muted p-[3px] !shadow-none">
          {TABS.map((id) => (
            <Tabs.Trigger
              key={id}
              value={id}
              ref={(node) => { tabRefs.current[id] = node; }}
              aria-label={`${label[id]}, ${shown[id].length}`}
              // `!`: the Themes sheet loads after Tailwind; its underline and inner padding are dropped.
              className={cn(
                'group !h-8 rounded-[7px] !px-3 !text-[13px] before:!hidden [&>span]:!bg-transparent [&>span]:!p-0',
                '!text-zinc-600 data-[state=active]:bg-card data-[state=active]:font-semibold data-[state=active]:!text-foreground data-[state=active]:shadow-sm dark:!text-zinc-400 dark:data-[state=active]:!text-foreground'
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                {label[id]}
                <span className="min-w-5 rounded-md bg-zinc-100 px-1.5 text-center text-[11px] font-semibold tabular-nums text-muted-foreground group-data-[state=active]:bg-muted dark:bg-zinc-800">
                  {shown[id].length}
                </span>
              </span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <div className="relative min-w-[220px] max-w-[420px] flex-1">
          <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            className="h-9 rounded-[9px] pl-[35px]"
            aria-label={t('Search')}
            placeholder={t('A store, a ticket…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          aria-label={t('Service desk')}
          value={desk}
          onChange={(e) => setDesk(e.target.value)}
          className="h-9 rounded-[9px] border border-input bg-background px-3 text-[13px] text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="all">{t('All service desks')}</option>
          {Object.entries(DESK_NAME).map(([sd, name]) => <option key={sd} value={sd}>{name}</option>)}
        </select>
      </div>
      {TABS.map((id) => (
        <Tabs.Content key={id} value={id} className="overflow-x-auto">
          <ReferentialBoundary message={t('This tab could not be displayed.')}>{panels[id]}</ReferentialBoundary>
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
};

export default AlignmentTabs;
